import { Box, ButtonV2, Flex } from '@ghq-abi/design-system';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { Ticket } from '~/entities/MyTickets/types';
import { ArrowExpand } from '~/shared/components/icons';
import { GRID_CONSTANTS } from '~/shared/constants';
import { DynamicHoverFactory } from '../DynamicHover';
import { ListCardHeader } from './ListCardHeader';

type ListCardProps = {
  ticket: Ticket;
};

export function ListCard({ ticket }: ListCardProps) {
  const router = useRouter();
  const [hovered, setHovered] = useState(false);

  const {
    id,
    title,
    status,
    ticketNumber,
    description,
    businessUnit,
    zone,
    employee,
    formType,
    category,
    subcategory,
    dynamicHoverData,
    createDate,
  } = ticket;

  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);

  const handleViewDetails = useCallback(() => {
    router.push(`/tickets/my-tickets/${id}`);
  }, [router, id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleViewDetails();
      }
    },
    [handleViewDetails]
  );

  const isOpen = hovered;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleViewDetails}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      css={{
        minHeight: GRID_CONSTANTS.LIST_CARD_MIN_HEIGHT,
        backgroundColor: '$white',
        border: `1px solid ${GRID_CONSTANTS.BORDER_COLOR}`,
        borderRadius: GRID_CONSTANTS.BORDER_RADIUS,
        padding: '$4',
        boxShadow: isOpen ? GRID_CONSTANTS.SHADOW_HOVER : '$sm',
        transition: `box-shadow ${GRID_CONSTANTS.TRANSITION_DURATION_LONG} ease, transform ${GRID_CONSTANTS.TRANSITION_DURATION_LONG} ease`,
        zIndex: isOpen ? 10 : 1,
        position: 'relative',
        overflow: 'hidden',
        cursor: 'pointer',
        outline: 'none',
        '&:focus-visible': {
          boxShadow: `0 0 0 3px $colors$primary300`,
        },
      }}
    >
      <Flex align="center" css={{ width: '$full', paddingRight: '$10' }}>
        <ListCardHeader
          title={title}
          status={status}
          rawStatus={ticket.rawStatus}
          ticketNumber={ticketNumber}
          employeeName={employee.name}
          businessUnit={businessUnit}
          zone={zone}
          category={category}
          subcategory={subcategory}
          formType={formType}
          createdDate={createDate}
        />
      </Flex>

      <ButtonV2
        aria-label="Toggle details"
        variant="secondary"
        size="md"
        onClick={(e) => {
          e.stopPropagation();
          handleViewDetails();
        }}
        css={{
          position: 'absolute',
          top: '$4',
          right: '$4',
          padding: '$1 $2',
          border: 'none',
          boxShadow: 'none',
          color: '$black',
          cursor: 'pointer',
        }}
      >
        <ArrowExpand />
      </ButtonV2>

      <Box
        aria-hidden={!isOpen}
        css={{
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? 'translateY(0)' : 'translateY(-4px)',
          maxHeight: isOpen ? GRID_CONSTANTS.HOVER_MAX_HEIGHT : '0px',
          transition: `opacity ${GRID_CONSTANTS.TRANSITION_DURATION_LONG} ease, transform ${GRID_CONSTANTS.TRANSITION_DURATION_LONG} ease, max-height ${GRID_CONSTANTS.TRANSITION_DURATION_LONG} ease`,
          overflow: 'hidden',
          willChange: 'opacity, transform, max-height',
          pointerEvents: isOpen ? 'auto' : 'none',
          marginTop: '$3',
        }}
      >
        <DynamicHoverFactory
          dynamicData={dynamicHoverData}
          employeeId={employee.employeeId}
          band={employee.band}
          positionTitle={employee.positionTitle}
          description={description}
        />
      </Box>
    </Box>
  );
}